import { createKeyedStore } from '@/common/utils/keyedStore';

/**
 * Bumped once per reconciliation sweep; every key checked in that sweep is stamped with the same
 * number, whether or not its data changed. Lets a consumer holding two related keys tell whether
 * one's still-standing offer was reconfirmed at least as recently as the other's newest offer,
 * instead of assuming two present offers are automatically mutually consistent.
 */
let currentRound = 0;

// clearOnUnsubscribe stays false — swept explicitly by itemsTreeSetConsistency.ts's
// startQueryRemovalCleanup, like its sibling per-key stores.
const store = createKeyedStore<number>({ clearOnUnsubscribe: false });

export const beginReconciliationRound = (): number => ++currentRound;

/**
 * True if `round` is still current for this key — false means a newer sweep already landed and
 * this response must be discarded, not classified/staged (sweeps can resolve out of order).
 * Notifies even when unchanged, since a bare reconfirmation still matters to a consumer waiting
 * on two keys' mutual coherence.
 */
export const recordReconciliationCheck = (queryKeyString: string, round: number): boolean => {
  const lastChecked = store.get(queryKeyString) ?? 0;
  if (round <= lastChecked) return false;
  store.set(queryKeyString, round);
  return true;
};

export const getLastCheckedRound = (queryKeyString: string): number | undefined =>
  store.get(queryKeyString);

export const subscribeReconciliationRound = (
  queryKeyString: string,
  listener: () => void,
): (() => void) => store.subscribe(queryKeyString, listener);

export const clearReconciliationRound = (queryKeyString: string): void =>
  store.delete(queryKeyString);
